import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useClub } from '@/contexts/ClubContext';
import AppShellSkeleton from '@/components/AppShellSkeleton';

interface ProtectedRouteProps {
  allowedRoles?: Array<'admin' | 'coach' | 'member'>;
  requiresClub?: boolean;
  requiresSuperAdmin?: boolean;
}

export default function ProtectedRoute({
  allowedRoles,
  requiresClub = false,
  requiresSuperAdmin = false,
}: ProtectedRouteProps) {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const { club, activeClubId, isLoading: clubLoading, hasLoadedInitial, isRefreshing, members, currentClubRole } = useClub();
  const hasClubSelection = Boolean(activeClubId || club);

  if (isLoading) {
    return <AppShellSkeleton />;
  }

  // Not logged in
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.needsConsent && location.pathname !== '/consent') {
    return <Navigate to="/consent" replace />;
  }

  if (requiresSuperAdmin) {
    if (!user.isSuperAdmin) {
      return <Navigate to="/select-club" replace />;
    }
    return <Outlet />;
  }

  if (
    user.isSuperAdmin &&
    !user.needsConsent &&
    location.pathname !== '/consent' &&
    !location.pathname.startsWith('/super-admin')
  ) {
    return <Navigate to="/super-admin" replace />;
  }

  const membershipRole = currentClubRole || members.find(
    (m) =>
      m.id === String(user.id) ||
      m.userId === String(user.id) ||
      (m.email && user.email && m.email.toLowerCase() === user.email.toLowerCase()),
  )?.role;
  const ownerRole =
    club?.adminId && String(club.adminId) === String(user.id)
      ? 'admin'
      : null;
  const effectiveClubRole = membershipRole || ownerRole;
  const preferClubRoleForDashboard = Boolean(allowedRoles) && !requiresClub && Boolean(activeClubId);
  const roleForAccessRaw = requiresClub
    ? effectiveClubRole
    : preferClubRoleForDashboard
      ? (effectiveClubRole || null)
      : (effectiveClubRole || user.role);
  const roleForAccess = roleForAccessRaw === 'player' ? 'member' : roleForAccessRaw;
  if (Boolean(allowedRoles) && !requiresClub && (!hasLoadedInitial || clubLoading || isRefreshing)) {
    return <AppShellSkeleton />;
  }
  if (requiresClub && (!hasLoadedInitial || clubLoading || isRefreshing)) {
    return <AppShellSkeleton />;
  }

  if (requiresClub && !hasClubSelection) {
    return <Navigate to="/select-club" replace />;
  }

  const awaitingRole =
    Boolean(allowedRoles) &&
    !roleForAccess &&
    (
      clubLoading ||
      !hasLoadedInitial ||
      isRefreshing ||
      (preferClubRoleForDashboard && (!club || members.length === 0))
    );

  if (awaitingRole) {
    return <AppShellSkeleton />;
  }

  // Role check
  if (allowedRoles && (!roleForAccess || !allowedRoles.includes(roleForAccess))) {
    return <Navigate to="/not-found" replace />;
  }

  return <Outlet />;
}
